import BaseController from "de/realcore/realseats/controller/BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Table from "sap/m/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import FilterBar from "sap/ui/comp/filterbar/FilterBar";
import type FilterItem from "sap/ui/comp/filterbar/FilterItem";
import type { BookingModel } from "../src/types/";
import ComboBox from "sap/m/ComboBox";
import type Event from "sap/ui/base/Event";
import SearchField from "sap/m/SearchField";
import DatePicker from "sap/m/DatePicker";
import Dialog from "sap/m/Dialog";
import Control from "sap/ui/core/Control";
import Fragment from "sap/ui/core/Fragment";
import MessageBox from "sap/m/MessageBox";
import formatter from "../src/utils/formatter";
import Log from "sap/base/Log";
import { getMaxBookingDate } from "../src/utils/TimeUtils";
import BusyIndicator from "sap/ui/core/BusyIndicator";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import ResourceBundle from "sap/base/i18n/ResourceBundle";
import Popover from "sap/m/Popover";
import Sorter from "sap/ui/model/Sorter";

/**
 * @namespace de.realcore.realseats.controller
 */
export default class MyBookings extends BaseController {
    public formatter = formatter;
    private _oBookingsModel!: JSONModel;
    private _oFilterBar!: FilterBar;
    private _oTable!: Table;
    private cancelDialog!: Dialog;
    private _oResourceBundle: ResourceBundle;
    private _roomTypesPopover: Popover;

    public onInit(): void {
        this._oResourceBundle = (
            this.getOwnerComponent().getModel("i18n") as ResourceModel
        ).getResourceBundle() as ResourceBundle;

        this.getOwnerComponent()
            .getRouter()
            .getRoute("MyBookings")
            .attachPatternMatched(this._onRouteMatched.bind(this));

        // Initialize DialogData model for date picker constraints
        const dialogData = {
            minDate: new Date(),
            maxDate: new Date(getMaxBookingDate()) // Create fresh Date object to prevent mutation
        };
        const dialogDataModel = new JSONModel(dialogData);
        this.getView().setModel(dialogDataModel, "DialogData");
    }

    /**
     * Fetches current user's bookings using the new API service
     * Follows Clean Code principles with clear method names and error handling
     */
    public async getMyBookings(): Promise<void> {
        try {
            // Initialize model with loading state
            if (!this._oBookingsModel) {
                this._oBookingsModel = new JSONModel({
                    myBookings: [],
                    isLoading: true
                });
                this.getView().setModel(this._oBookingsModel, "BookingsModel");
            } else {
                this._oBookingsModel.setProperty("/isLoading", true);
            }

            const apiService = this.getApiService();
            const myBookings = await apiService.booking.fetchMyBookings();

            // Process bookings to ensure proper grouping and sorting
            const processedBookings = myBookings.map((booking: any) => {
                return {
                    ...booking,
                    locationGroup:
                        booking?.seat?.room?.floorPlan?.location?.name || "Unknown Location"
                };
            });

            this._oBookingsModel.setData({
                myBookings: processedBookings,
                isLoading: false
            });

            this._onBookingsDataLoaded();
            BusyIndicator.hide();
        } catch (error) {
            console.error("Error fetching my bookings:", error);
            // Set loading to false even on error and show empty state
            if (this._oBookingsModel) {
                this._oBookingsModel.setData({
                    myBookings: [],
                    isLoading: false
                });
            }
            BusyIndicator.hide();
            // Handle error appropriately - could show a message to user
        }
    }

    private _onRouteMatched(): void {
        const oView = this.getView();
        this._oFilterBar = oView.byId("myBookingsFilterBar") as FilterBar;
        this._oTable = oView.byId("tableBookings") as Table;

        void this.getMyBookings();
    }

    private _configureTableGrouping(): void {
        const oBinding = this._oTable.getBinding("items") as ListBinding;
        if (oBinding) {
            // Create custom sorter with grouping function
            const oSorter = new Sorter("startTime", false, (oContext: any) => {
                const sStartTime = oContext.getProperty("startTime");
                return formatter.formatDateForGrouping(sStartTime);
            });

            oBinding.sort(oSorter);
        }
    }

    private _onBookingsDataLoaded(): void {
        const oBookingTypeCombo = this.byId("idBookingTypeComboBox") as ComboBox;
        if (oBookingTypeCombo) {
            oBookingTypeCombo.setSelectedKey("currently");
        }

        const oTableBinding = this._oTable.getBinding("items") as ListBinding;
        if (oTableBinding) {
            // Show loading indicator during initial filtering
            this._oBookingsModel.setProperty("/isLoading", true);

            const aDefaultFilters = this._getBookingTypeFilters("currently");
            oTableBinding.filter(aDefaultFilters);

            // Configure custom grouping after filtering
            this._configureTableGrouping();

            // Hide loading indicator after initial filtering is complete
            setTimeout(() => {
                this._oBookingsModel.setProperty("/isLoading", false);
            }, 100);
        }
    }

    public onSearch(): void {
        const aFilterItems = this._oFilterBar.getAllFilterItems(true) as FilterItem[];
        const aTableFilters = aFilterItems.reduce((aResult: Filter[], oFilterItem: FilterItem) => {
            const sFilterName = oFilterItem.getName();
            const oFilterControl = oFilterItem.getControl() as SearchField | ComboBox | DatePicker;
            let sValue = "";

            if (sFilterName === "Search") {
                sValue = (oFilterControl as SearchField).getValue();
                if (sValue) {
                    // Free text search across multiple fields
                    const aSearchFilters = [
                        new Filter("id", FilterOperator.Contains, sValue),
                        new Filter("seat/room/name", FilterOperator.Contains, sValue),
                        new Filter({
                            filters: [
                                new Filter(
                                    "bookingUser/firstName",
                                    FilterOperator.Contains,
                                    sValue
                                ),
                                new Filter("bookingUser/lastName", FilterOperator.Contains, sValue)
                            ],
                            and: false
                        })
                    ];
                    aResult.push(new Filter({ filters: aSearchFilters, and: false }));
                }
            } else if (sFilterName === "BookingType") {
                sValue = (oFilterControl as ComboBox).getSelectedKey();
                if (sValue && sValue !== "all") {
                    const aTypeFilters = this._getBookingTypeFilters(sValue);
                    aResult.push(...aTypeFilters);
                }
            } else if (sFilterName === "Date") {
                const oDate = (oFilterControl as DatePicker).getDateValue();
                if (oDate instanceof Date && !isNaN(oDate.getTime())) {
                    const oStart = new Date(oDate);
                    oStart.setHours(0, 0, 0, 0);

                    const oEnd = new Date(oDate);
                    oEnd.setHours(23, 59, 59, 999);

                    aResult.push(
                        new Filter(
                            "startTime",
                            FilterOperator.BT,
                            oStart.toISOString(),
                            oEnd.toISOString()
                        )
                    );
                }
            }

            return aResult;
        }, []);

        const oTableBinding = this._oTable.getBinding("items") as ListBinding;

        // Show loading indicator during filtering
        this._oBookingsModel.setProperty("/isLoading", true);

        oTableBinding.filter(aTableFilters);

        // Apply custom grouping after filtering
        this._configureTableGrouping();

        // Hide loading indicator after filtering is complete
        setTimeout(() => {
            this._oBookingsModel.setProperty("/isLoading", false);
        }, 100);
    }

    private _getBookingTypeFilters(sBookingType: string): Filter[] {
        // Create current time in the same format as booking times (local datetime without timezone)
        const now = new Date();

        // Format as local ISO string without timezone (YYYY-MM-DDTHH:mm:ss)
        const sNowLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 19);

        const aFilters: Filter[] = [];

        switch (sBookingType) {
            case "currently":
                // Show bookings that are not cancelled AND either:
                // 1. Currently active (now is between startTime and endTime)
                // 2. Future bookings (startTime is in the future)
                aFilters.push(
                    new Filter("cancelled", FilterOperator.EQ, false),
                    new Filter({
                        filters: [
                            // Currently active bookings
                            new Filter({
                                filters: [
                                    new Filter("startTime", FilterOperator.LE, sNowLocal),
                                    new Filter("endTime", FilterOperator.GT, sNowLocal)
                                ],
                                and: true
                            }),
                            // Future bookings
                            new Filter("startTime", FilterOperator.GT, sNowLocal)
                        ],
                        and: false
                    })
                );
                break;

            case "historie":
                // Show bookings that are not cancelled AND have ended (endTime < now)
                aFilters.push(
                    new Filter("cancelled", FilterOperator.EQ, false),
                    new Filter("endTime", FilterOperator.LT, sNowLocal)
                );
                break;

            case "cancelled":
                aFilters.push(new Filter("cancelled", FilterOperator.EQ, true));
                break;
        }

        return aFilters;
    }

    onCancelBooking(oEvent: Event): void {
        const source = oEvent.getSource();

        if (!("getBindingContext" in source) || typeof source.getBindingContext !== "function") {
            Log.error("Event source does not support getBindingContext.");
            return;
        }

        const oSource = source as Control;
        const oContext = oSource.getBindingContext("BookingsModel");
        const oView = this.getView();

        if (!this.cancelDialog) {
            Fragment.load({
                id: "myBookings",
                name: "de.realcore.realseats.view.fragment.CancelBookingDialog",
                controller: this
            })
                .then((oFragment) => {
                    if (!oFragment) {
                        Log.error("Fragment not loaded");
                        return;
                    }

                    this.cancelDialog = oFragment as Dialog;
                    this.cancelDialog.setBindingContext(oContext, "BookingsModel");
                    oView.addDependent(this.cancelDialog);
                    this.cancelDialog.open();
                })
                .catch((error) => {
                    Log.error("Failed to load the CancelBookingDialog fragment", error);
                });
        } else {
            this.cancelDialog.setBindingContext(oContext, "BookingsModel");
            this.cancelDialog.open();
        }
    }

    public onCancelDialog(): void {
        if (this.cancelDialog) {
            this.cancelDialog.close();
        }
    }

    /**
     * Handles confirmation of booking cancellation
     * Uses the new API service for clean separation of concerns
     */
    public async onConfirmCancellation(): Promise<void> {
        const oDialog = this.cancelDialog;
        const oContext = oDialog?.getBindingContext("BookingsModel");
        const oBooking = oContext?.getObject() as BookingModel;
        const bookingId = oBooking?.id;
        const cancelBookingDesc =
            oBooking?.cancelDescription || "No reason provided for cancellation.";

        try {
            const apiService = this.getApiService();
            await apiService.booking.cancelBooking(bookingId, cancelBookingDesc);

            MessageBox.success("Die Buchung wurde erfolgreich storniert.");
            this.cancelDialog?.close();

            // Refresh the bookings list
            await this.getMyBookings();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            MessageBox.error("Fehler beim Stornieren: " + errorMessage);
        }
    }

    /**
     * Shows a popover with all room types for a room when the link is clicked
     * @param oEvent The press event
     */
    public async onShowRoomTypesPopover(oEvent: Event): Promise<void> {
        const oLink = oEvent.getSource() as Control;
        const oContext = oLink.getBindingContext("BookingsModel");

        if (!oContext) {
            return;
        }

        const oBooking = oContext.getObject() as BookingModel;
        const aRoomTypes = oBooking.seat?.room?.roomTypes || [];

        // Create popover if it doesn't exist
        if (!this._roomTypesPopover) {
            this._roomTypesPopover = (await Fragment.load({
                id: this.getView()?.getId(),
                name: "de.realcore.realseats.view.fragment.RoomTypesPopover",
                controller: this
            })) as Popover;
            this.getView()?.addDependent(this._roomTypesPopover);
        }

        // Set the room types data to the popover
        const oPopoverModel = new JSONModel({ roomTypes: aRoomTypes });
        this._roomTypesPopover.setModel(oPopoverModel, "PopoverRoomTypesModel");

        // Open popover
        this._roomTypesPopover.openBy(oLink);
    }

    /**
     * Clean up dialogs when the controller is destroyed
     */
    public onExit(): void {
        if (this.cancelDialog) {
            this.cancelDialog.destroy();
            this.cancelDialog = undefined;
        }
        if (this._roomTypesPopover) {
            this._roomTypesPopover.destroy();
            this._roomTypesPopover = undefined;
        }
    }
}
